import React, { useState, useEffect } from 'react'
import './PopupDropdown.css'
import { MdClear } from "react-icons/md"
import Label from './Label'
import CircularProgress from '@mui/material/CircularProgress'
import CheckBox from './CheckBox'

const PopupDropdown = ({
  options,
  optionsPromise,
  value,
  onChange,
  title,
  fontSize = "0.75rem",
  inputBoxHeight = "auto",
  searchBar = false,
  isMobile = false,
  labelView = false,
  labelColors,
  formatType = "",
  excludeOptions,
  padding = "7.5px 10px",
  icon = null,
  width = "300px",
  customButton = null,
  allowHTML = false,
  multiselect = false,
  disabled = false,
  clearable = false,
  hasBorder = true
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [loadedOptions, setLoadedOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Initialize loadedOptions with static options if provided
    if (options && !optionsPromise) {
      setLoadedOptions(options);
    }
  }, [options]);

  const handleToggle = async () => {
    if (disabled) return
    const nextOpenState = !isOpen;
    setIsOpen(nextOpenState);
    
    // If we're opening the dropdown and we have an optionsPromise, load the options
    if (nextOpenState && optionsPromise && loadedOptions.length === 0) {
      setIsLoading(true);
      try {
        const fetchedOptions = await optionsPromise();
        if (fetchedOptions && Array.isArray(fetchedOptions)) {
          setLoadedOptions(fetchedOptions);
        }
      } catch (error) {
        console.error("Error loading options:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  const handleSelect = (option) => {
    if (disabled) return
    if (multiselect) {
      const currentValues = Array.isArray(value) ? value : [];
      const isCurrentlySelected = currentValues.some(v => {
        if (typeof v === 'object' && v !== null && typeof option === 'object' && option !== null) {
          // If both have type and id, compare both for uniqueness
          if (v.type && v.id && option.type && option.id) {
            return v.id === option.id && v.type === option.type;
          }
          // Otherwise just compare ids
          return v.id === option.id;
        }
        return v === option;
      });
      
      let newValues;
      if (isCurrentlySelected) {
        // Remove the option
        newValues = currentValues.filter(v => {
          if (typeof v === 'object' && v !== null && typeof option === 'object' && option !== null) {
            // If both have type and id, compare both for uniqueness
            if (v.type && v.id && option.type && option.id) {
              return !(v.id === option.id && v.type === option.type);
            }
            // Otherwise just compare ids
            return v.id !== option.id;
          }
          return v !== option;
        });
      } else {
        // Add the option
        newValues = [...currentValues, option];
      }
      
      onChange(newValues);
      // Don't close dropdown in multiselect mode
    } else {
      onChange(option);
      setIsOpen(false);
    }
  }

  // Helper function to get a unique key for an option
  const getUniqueKey = (option) => {
    if (typeof option === 'object' && option !== null) {
      // If the option has both type and id (like our contact/company items), create a unique key
      if (option.type && option.id) {
        return `${option.type}-${option.id}`;
      }
      // Otherwise use just the id
      return option.id;
    }
    return option;
  }

  // Helper function to get the label color key
  const getLabelColorKey = (val) => {
    if (!val) return null;
    if (typeof val === 'object' && val !== null && val.id) {
      return val.id;
    }
    return val;
  }

  // Helper function to get the display value
  const getDisplayValue = (option) => {
    if (typeof option === 'object' && option !== null) {
      // Handle contact/company types specifically
      if (option.type === 'contact') {
        return [option.first_name, option.last_name].filter(Boolean).join(' ') || 'Unknown Contact'
      } else if (option.type === 'company') {
        return option.company_name || option.name || 'Unknown Company'
      } else if (option.name) {
        return option.name
      } else {
        return Object.entries(option)
          .filter(([key]) => key !== 'id' && key !== 'type' && (!excludeOptions || !excludeOptions.includes(key)))
          .map(([_, value]) => value)
          .join(' ')
      }
    }
    return option
  }

  // Helper function to get plain text version of display value for searching
  const getSearchableText = (option) => {
    const displayValue = getDisplayValue(option);
    // Create a temporary element to strip HTML tags if the content is HTML
    if (allowHTML && typeof displayValue === 'string') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = displayValue;
      return tempDiv.textContent || tempDiv.innerText || '';
    }
    return displayValue;
  }

  const normalizeSearchText = (text) => (
    String(text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
  )

  const getSearchRank = (option) => {
    const normalizedSearchTerm = normalizeSearchText(searchTerm)
    if (!normalizedSearchTerm) return 0

    const normalizedText = normalizeSearchText(getSearchableText(option))
    if (!normalizedText) return -1
    if (normalizedText === normalizedSearchTerm) return 0
    if (normalizedText.startsWith(normalizedSearchTerm)) return 1
    if (normalizedText.split(/[\s-]+/).some((part) => part.startsWith(normalizedSearchTerm))) return 2

    const isShortSearch = normalizedSearchTerm.length < 4
    if (!isShortSearch && normalizedText.includes(normalizedSearchTerm)) return 3

    return -1
  }

  // Helper function to check if an option is selected
  const isSelected = (option) => {
    if (multiselect) {
      const currentValues = Array.isArray(value) ? value : [];
      return currentValues.some(v => {
        if (typeof v === 'object' && v !== null && typeof option === 'object' && option !== null) {
          // If both have type and id, compare both for uniqueness
          if (v.type && v.id && option.type && option.id) {
            return v.id === option.id && v.type === option.type;
          }
          // Otherwise just compare ids
          return v.id === option.id;
        }
        return v === option;
      });
    } else {
      if (typeof option === 'object' && option !== null && typeof value === 'object' && value !== null) {
        // If both have type and id, compare both for uniqueness
        if (value.type && value.id && option.type && option.id) {
          return value.id === option.id && value.type === option.type;
        }
        // Otherwise just compare ids
        return option.id === value.id;
      }
      return option === value;
    }
  }

  // Helper function to remove an item in multiselect mode
  const removeSelectedItem = (itemToRemove, e) => {
    e.stopPropagation();
    if (multiselect) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.filter(v => {
        if (typeof v === 'object' && v !== null && typeof itemToRemove === 'object' && itemToRemove !== null) {
          // If both have type and id, compare both for uniqueness
          if (v.type && v.id && itemToRemove.type && itemToRemove.id) {
            return !(v.id === itemToRemove.id && v.type === itemToRemove.type);
          }
          // Otherwise just compare ids
          return v.id !== itemToRemove.id;
        }
        return v !== itemToRemove;
      });
      onChange(newValues);
    }
  }

  const hasValue = multiselect
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== undefined && value !== ''

  const canClear = clearable && !multiselect && hasValue && !disabled

  const handleClear = (e) => {
    e.stopPropagation()
    onChange('')
    setIsOpen(false)
  }

  // Render option content (with or without HTML)
  const renderOptionContent = (option) => {
    const content = getDisplayValue(option);
    if (allowHTML && typeof content === 'string') {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
    return content;
  }

  // Render the display value for multiselect
  const renderMultiselectDisplay = () => {
    const currentValues = Array.isArray(value) ? value : [];
    
    if (currentValues.length === 0) {
      return title;
    }
    
    return (
      <div className="multiselect-display">
        <div className="multiselect-items">
          {currentValues.map((item, index) => (
            <div key={getUniqueKey(item)} className="multiselect-tag">
              <span>{getDisplayValue(item)}</span>
              <button 
                className="multiselect-remove"
                onClick={(e) => removeSelectedItem(item, e)}
                type="button"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Determine which options to use
  const displayOptions = optionsPromise ? loadedOptions : (options || []);

  return (
    <div className={`popup-dropdown ${isMobile ? "mobile" : ""} ${formatType} ${multiselect ? "multiselect" : ""} ${disabled ? 'disabled' : ''} ${!hasBorder ? 'borderless' : ''}`}>
      {customButton ? (
        <div onClick={handleToggle}>{customButton}</div>
      ) : (
        <div className={`popup-dropdown__selected ${canClear ? 'popup-dropdown__selected--clearable' : ''}`} onClick={handleToggle} style={{ fontSize: isMobile ? "1rem" : fontSize, height: isMobile ? "60px" : inputBoxHeight, padding: padding, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <div className="popup-dropdown__selected-value">
            {icon ? (
              <div className="popup-dropdown__icon" title={title}>{icon}</div>
            ) : (
              multiselect ? (
                renderMultiselectDisplay()
              ) : (
                hasValue ? (
                  labelView ? (
                    <Label
                      title={getDisplayValue(value)}
                      fontSize={isMobile ? "1rem" : fontSize}
                      labelColor={labelColors[getLabelColorKey(value)]}
                    />
                  ) : (
                    allowHTML ? 
                    <div dangerouslySetInnerHTML={{ __html: getDisplayValue(value) }} /> : 
                    getDisplayValue(value)
                  )
                ) : (
                  title
                )
              )
            )}
          </div>
          {canClear && (
            <button
              type="button"
              className="popup-dropdown__selected-clear-button"
              onClick={handleClear}
              aria-label="Clear selection"
              title="Clear selection"
            >
              <MdClear />
            </button>
          )}
        </div>
      )}
      {
        isOpen && (
          <div className="popup-dropdown__overlay" onClick={handleToggle}>
            <div className="popup-dropdown__popup" style={{ width: width }} onClick={(e) => e.stopPropagation()}>
              <div className="popup-dropdown__header">
                <div className="title">{title}</div>
                {multiselect && (
                  <button 
                    className="popup-dropdown__done-button"
                    onClick={handleToggle}
                  >
                    Done
                  </button>
                )}
              </div>
              {searchBar && (
                <div className="popup-dropdown__search-container">
                  <input
                    className="popup-dropdown__search"
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="popup-dropdown__clear-button"
                      onClick={() => {
                        setSearchTerm("")
                      }}
                    >
                      <MdClear />
                    </button>
                  )}
                </div>
              )}
              <div className="popup-dropdown__options">
                {isLoading ? (
                  <div className="popup-dropdown__loading">
                    <CircularProgress size={24} color="inherit"/>
                    <span>Loading options...</span>
                  </div>
                ) : displayOptions.length === 0 ? (
                  <div className="popup-dropdown__no-options">No options available</div>
                ) : (
                  (() => {
                    // Filter options based on search term
                    const filteredOptions = displayOptions
                      .map((option, index) => ({ option, index, rank: getSearchRank(option) }))
                      .filter(({ rank }) => rank >= 0)
                      .sort((a, b) => a.rank - b.rank || a.index - b.index)
                      .map(({ option }) => option);

                    // If multiselect, separate selected and unselected options
                    if (multiselect) {
                      const currentValues = Array.isArray(value) ? value : [];
                      const selectedOptions = filteredOptions.filter(option => isSelected(option));
                      const unselectedOptions = filteredOptions.filter(option => !isSelected(option));
                      
                      // Combine with selected options first
                      const sortedOptions = [...selectedOptions, ...unselectedOptions];
                      
                      return sortedOptions.map((option) => (
                        <div
                          key={getUniqueKey(option)}
                          className={`popup-dropdown__option ${isSelected(option) ? 'selected' : ''} ${multiselect ? 'multiselect-option' : ''}`}
                          onClick={() => handleSelect(option)}
                        >
                          {multiselect && (
                            <div className="multiselect-checkbox">
                              <CheckBox 
                                state={isSelected(option)} 
                                setState={(newState) => {
                                  // The CheckBox component toggles internally, so we just call handleSelect
                                  handleSelect(option);
                                }}
                                height="20px"
                                darkMode={true}
                              />
                            </div>
                          )}
                          <div className="option-content">
                            {renderOptionContent(option)}
                          </div>
                        </div>
                      ));
                    } else {
                      // For single select, just show filtered options normally
                      return filteredOptions.map((option) => (
                        <div
                          key={getUniqueKey(option)}
                          className={`popup-dropdown__option ${isSelected(option) ? 'selected' : ''}`}
                          onClick={() => handleSelect(option)}
                        >
                          <div className="option-content">
                            {renderOptionContent(option)}
                          </div>
                        </div>
                      ));
                    }
                  })()
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  )
}

export default PopupDropdown


